import { css, SerializedStyles } from '@emotion/react';
import styled, { StyledComponent } from '@emotion/styled';
import type {
  WThemeComponentFontView,
  WThemeComponentColorView,
  WThemeComponentBackgroundView,
  WAbsoluteAssetSizeView,
  WLengthView,
  WResponsiveAssetSizeViewWAbsoluteAssetSizeView,
  WInFlowThemeComponentAssetView,
  WInFlowAssetSizeView,
  WInFlowThemeComponentAssetSummaryView,
  WThemeComponentBackgroundImageView,
} from '@zola/svc-web-api-ts-client';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import _pick from 'lodash/pick';
import { isDarkColor, hexToRgb } from 'pages/publicStyleUtils/utils.styles';
import COLORS from '@zola/zola-ui/src/styles/emotion/colors';
import { Z_INDEX_MAP } from '../zIndexConstants';

export { default as mapWeddingToComponents } from './mapWeddingToComponents';

/** all fields required; not optional */
export type FullComponentFontView = {
  fontFamily: string;
  isCursive: boolean;
  fontSize: number;
  letterSpacing: string | number;
  lineHeight: number;
  textTransform: WThemeComponentFontView.TextTransformEnum;
  color: string;
  fontWeight: WThemeComponentFontView.FontWeightEnum;
};

export type PartialComponentFontView = {
  color?: string;
  font_size?: number;
  line_height?: number;
  text_transform?: WThemeComponentFontView.TextTransformEnum;
  font_weight?: WThemeComponentFontView.FontWeightEnum;
  letter_spacing?: string | number;
  name?: string;
  cursive?: boolean;
  font_family?: {
    name?: string;
    line_height?: number;
    cursive?: boolean;
  };
};

/** fills font properties with values from fallback if missing */
export const fillFontProperties = (
  defaultValues: FullComponentFontView,
  values?: PartialComponentFontView
): FullComponentFontView => {
  return {
    fontFamily: values?.font_family?.name || values?.name || defaultValues.fontFamily,
    isCursive: values?.font_family?.cursive || values?.cursive || defaultValues.isCursive,
    fontSize: values?.font_size || defaultValues.fontSize,
    letterSpacing: values?.letter_spacing || defaultValues.letterSpacing,
    lineHeight: values?.line_height || defaultValues.lineHeight,
    textTransform: values?.text_transform || defaultValues.textTransform,
    color: values?.color || defaultValues.color,
    fontWeight: values?.font_weight || defaultValues.fontWeight,
  };
};

export const mapFullFontViewToSerializedStyles = (
  f: FullComponentFontView,
  responsiveFontSize?: boolean
): SerializedStyles => {
  /**
   * Font weight sent as weight name
   *
   * Map according to OpenType specification:
   * https://docs.microsoft.com/en-us/typography/opentype/spec/os2#usweightclass
   */
  const mapWeightNameToNumericalValue = (n: WThemeComponentFontView.FontWeightEnum): number => {
    switch (n) {
      case ('THIN' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 100;
      case ('EXTRALIGHT' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 200;
      case ('LIGHT' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 300;
      case ('NORMAL' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 400;
      case ('MEDIUM' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 500;
      case ('SEMIBOLD' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 600;
      case ('BOLD' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 700;
      case ('EXTRABOLD' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 800;
      case ('BLACK' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 900;
      case ('EXTRABLACK' as unknown) as WThemeComponentFontView.FontWeightEnum:
        return 950;
      default:
        return 400;
    }
  };

  const getFontSize = () => {
    return `
      font-size: ${f.fontSize}px;
      ${
        responsiveFontSize
          ? `
      ${MEDIA_QUERY.MOBILE} {
        font-size: ${Math.ceil(f.fontSize * 0.8)}px;
      }`
          : ''
      }
    `;
  };

  return css`
    font-family: '${f.fontFamily}';
    color: #${f.color};
    font-weight: ${mapWeightNameToNumericalValue(f.fontWeight)};
    text-transform: ${
      f.isCursive ? 'none' : ((f.textTransform as unknown) as string).toLowerCase()
    };
    line-height: ${f.lineHeight};
    letter-spacing: ${f.letterSpacing > 0 ? `${f.letterSpacing}px` : 'normal'};
    ${getFontSize()}
  `;
};

export const mapFontValuesToSerializedStyles = (
  fallback: FullComponentFontView,
  partial?: PartialComponentFontView,
  responsiveFontSize?: boolean
): SerializedStyles => {
  return mapFullFontViewToSerializedStyles(
    fillFontProperties(fallback, partial),
    responsiveFontSize
  );
};

export const mapValuesToButtonStyles = (
  globalBodyFontFamily: string,
  accentColor?: string
): SerializedStyles => {
  const defaultAccentColor = COLORS.NAVY.substring(1);
  const isAccentColorDark = isDarkColor(accentColor || defaultAccentColor);
  const fontColor = isAccentColorDark ? '#FFFFFF' : '#000000';
  const accentColorRGB = hexToRgb(accentColor);
  const { r, g, b } = accentColorRGB || { r: 0, g: 0, b: 0 };
  const shadeFactor = 0.4;
  const darkenedColor = `rgb(${r * (1 - shadeFactor)}, ${g * (1 - shadeFactor)}, ${
    b * (1 - shadeFactor)
  })`;
  const lightenedColor = `rgb(${r}, ${g}, ${b}, ${shadeFactor})`;
  return css`
    background-color: rgb(${r}, ${g}, ${b});
    border-radius: 4px;
    border: none;
    padding: 16px;
    font-family: ${globalBodyFontFamily};
    color: ${fontColor};
    font-size: 16px;
    font-weight: 600;
    line-height: 16px;
    margin: 0;
    width: 200px;

    &:hover {
      background-color: ${isAccentColorDark ? darkenedColor : lightenedColor};
      color: ${fontColor};
      text-decoration: none;
    }
    &:disabled {
      opacity: 0.4;
    }
    text-align: center;
    &:visited {
      color: ${fontColor};
    }
    &:focus {
      text-decoration: none;
    }
  `;
};

export const mapLinkStylesToComponent = (
  f: FullComponentFontView,
  isDarkMode: boolean,
  backgroundColor?: WThemeComponentColorView
): StyledComponent<React.AnchorHTMLAttributes<HTMLAnchorElement>> => {
  return styled.a`
    display: inline-block;
    font-family: ${f.fontFamily};
    text-transform: ${f.textTransform};
    color: #${f.color};
    font-size: inherit;
    line-height: inherit;
    font-weight: 600;
    text-decoration: underline;
    background-color: transparent;
    transition: background-color 300ms;

    &:hover {
      text-decoration: none;
      color: ${isDarkMode ? COLORS.BLACK : `#${backgroundColor?.value || COLORS.BLACK}`};
      background-color: #${f.color};
    }
  `;
};

// Currently only handles hex color
export const mapButtonStylesToComponent = (
  globalBodyFontFamily: string,
  accentColor?: string
): StyledComponent<React.ButtonHTMLAttributes<HTMLButtonElement>> => {
  return styled.button`
    ${mapValuesToButtonStyles(globalBodyFontFamily, accentColor)}
  `;
};

const mapLengthToCSS = (l?: WLengthView): string => {
  if (!l) {
    return '';
  }
  const { unit, value } = l;
  if (unit === (('PERCENTAGE' as unknown) as WLengthView.UnitEnum)) {
    return `${value}%;`;
  }
  if (unit === (('REM' as unknown) as WLengthView.UnitEnum)) {
    return `${value}rem`;
  }
  return `${value}px`;
};

type BaseAssetStyles = Pick<
  WAbsoluteAssetSizeView,
  | 'width'
  | 'z_index_key'
  | 'margin_top'
  | 'margin_bottom'
  | 'margin_right'
  | 'margin_left'
  | 'display'
>;

const mapBaseAssetStylesToCSS = (b: BaseAssetStyles) => {
  const { width, z_index_key, margin_top, margin_bottom, margin_left, margin_right, display } = b;
  return `
    ${width ? `width: ${mapLengthToCSS(width)}` : ''};
    ${z_index_key ? `z-index: ${Z_INDEX_MAP[z_index_key]};` : ''}
    ${margin_top ? `margin-top: ${mapLengthToCSS(margin_top)};` : ''}
    ${margin_bottom ? `margin-bottom: ${mapLengthToCSS(margin_bottom)};` : ''}
    ${margin_right ? `margin-right: ${mapLengthToCSS(margin_right)};` : ''}
    ${margin_left ? `margin-left: ${mapLengthToCSS(margin_left)};` : ''}
    ${display ? `display: ${display};` : ''}
  `;
};

const mapAbsoluteAssetSizeViewToCSS = (abs: WAbsoluteAssetSizeView) => {
  const {
    width,
    z_index_key,
    margin_right,
    margin_left,
    margin_top,
    margin_bottom,
    top,
    right,
    bottom,
    left,
    display,
  } = abs;
  return `
    ${mapBaseAssetStylesToCSS({
      width,
      z_index_key,
      margin_top,
      margin_bottom,
      margin_left,
      margin_right,
      display,
    })}
    position: absolute;
    ${top ? `top: ${mapLengthToCSS(top)};` : ''}
    ${bottom ? `bottom: ${mapLengthToCSS(bottom)};` : ''}
    ${right ? `right: ${mapLengthToCSS(right)};` : ''}
    ${left ? `left: ${mapLengthToCSS(left)};` : ''}
  `;
};

const mapInFlowStylesToCSS = (inf?: WInFlowAssetSizeView) => {
  if (!inf) return '';
  const {
    width,
    height,
    z_index_key,
    margin_top,
    margin_bottom,
    margin_right,
    margin_left,
    display,
  } = inf;
  const styles = `
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      height: ${height?.value}px;
      ${mapBaseAssetStylesToCSS({
        width,
        z_index_key,
        margin_top,
        margin_bottom,
        margin_right,
        margin_left,
        display,
      })}
  `;
  return styles;
};

export const mapAbsoluteAssetViewToStyledImg = (
  a: WResponsiveAssetSizeViewWAbsoluteAssetSizeView
): StyledComponent<React.ImgHTMLAttributes<HTMLImageElement>> => {
  const { desktop, tablet, mobile, custom_breakpoints } = a || {};
  let modifiedTablet = tablet;
  if (desktop?.width?.unit === (('PIXEL' as unknown) as WLengthView.UnitEnum) && !tablet?.width) {
    modifiedTablet = {
      ...modifiedTablet,
      width: {
        unit: ('PIXEL' as unknown) as WLengthView.UnitEnum,
        value: Math.floor((desktop?.width.value * 2) / 3),
      },
    };
  }
  let modifiedMobile = mobile;
  if (desktop?.width?.unit === (('PIXEL' as unknown) as WLengthView.UnitEnum) && !mobile?.width) {
    modifiedMobile = {
      ...modifiedMobile,
      width: {
        unit: ('PIXEL' as unknown) as WLengthView.UnitEnum,
        value: Math.floor(desktop?.width.value / 3),
      },
    };
  }
  return styled.img`
    ${desktop && mapAbsoluteAssetSizeViewToCSS(desktop)}
    ${modifiedTablet && `${MEDIA_QUERY.TABLET} {${mapAbsoluteAssetSizeViewToCSS(modifiedTablet)}}`}
    ${modifiedMobile && `${MEDIA_QUERY.MOBILE} {${mapAbsoluteAssetSizeViewToCSS(modifiedMobile)}}`}
    ${
      custom_breakpoints
        ? css`
            ${Object.keys(custom_breakpoints).map(
              q =>
                `@media (max-width: ${q}px) {${mapAbsoluteAssetSizeViewToCSS(
                  custom_breakpoints[(q as unknown) as keyof typeof custom_breakpoints]
                )}}`
            )}
          `
        : ''
    }
  `;
};

export const mapPseudoElementToCSS = (
  e: WInFlowThemeComponentAssetView | undefined
): SerializedStyles | '' => {
  if (!e) return '';

  const {
    image_url,
    // option_type_value, TODO
    style,
  } = e;

  if (!style?.responsive_size) return '';

  const { desktop, tablet, mobile, custom_breakpoints } = style?.responsive_size || {};
  const { height, width } = desktop || {};
  const aspectRatio = height && width?.value ? height?.value / width?.value : 1;
  let modifiedTablet = tablet;
  if (width?.unit === (('PIXEL' as unknown) as WLengthView.UnitEnum) && !tablet?.width) {
    const tabletWidth = Math.floor((width?.value * 2) / 3);
    const tabletHeight = Math.floor(aspectRatio * tabletWidth);
    modifiedTablet = {
      ...modifiedTablet,
      width: { unit: ('PIXEL' as unknown) as WLengthView.UnitEnum, value: tabletWidth },
      height: { unit: ('PIXEL' as unknown) as WLengthView.UnitEnum, value: tabletHeight },
    };
  }
  let modifiedMobile = mobile;
  if (width?.unit === (('PIXEL' as unknown) as WLengthView.UnitEnum) && !mobile?.width) {
    const mobileWidth = Math.floor(width?.value / 3);
    const mobileHeight = Math.floor(aspectRatio * mobileWidth);
    modifiedMobile = {
      ...modifiedMobile,
      width: { unit: ('PIXEL' as unknown) as WLengthView.UnitEnum, value: mobileWidth },
      height: { unit: ('PIXEL' as unknown) as WLengthView.UnitEnum, value: mobileHeight },
    };
  }

  const orderedBreakpoints = Object.keys(custom_breakpoints || {}).sort();
  const otherBPDesktop = _pick(
    custom_breakpoints,
    orderedBreakpoints.filter(b => Number(b) > 1200)
  );
  const otherBPDesktopToTablet = _pick(
    custom_breakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 1200 && Number(b) > 992)
  );
  const otherBPTabletToMobile = _pick(
    custom_breakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 992 && Number(b) > 767)
  );
  const otherBPMobile = _pick(
    custom_breakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 767)
  );

  const getCustomBreakpointStyles = (b: Partial<{ [key: string]: WInFlowAssetSizeView }>) => {
    if (Object.keys(b).length === 0) {
      return '';
    }
    return css`
      ${Object.keys(b).map(
        q =>
          `@media (max-width: ${q}px) {${mapInFlowStylesToCSS(
            b[(q as unknown) as keyof typeof b]
          )}}`
      )}
    `;
  };

  return css`
    ${image_url ? `background-image: url(${image_url});` : ''}
    content: ' ';
    ${getCustomBreakpointStyles(otherBPDesktop)}
    ${mapInFlowStylesToCSS(desktop)}
    ${getCustomBreakpointStyles(otherBPDesktopToTablet)}
    ${modifiedTablet ? `${MEDIA_QUERY.TABLET} {${mapInFlowStylesToCSS(modifiedTablet)}}` : ''}
    ${getCustomBreakpointStyles(otherBPTabletToMobile)}
    ${modifiedMobile ? `${MEDIA_QUERY.MOBILE} {${mapInFlowStylesToCSS(modifiedMobile)}}` : ''}
    ${getCustomBreakpointStyles(otherBPMobile)}

  `;
};

export const mapInFlowAssetToCSS = (
  i?: WInFlowThemeComponentAssetSummaryView
): SerializedStyles | '' => {
  if (!i) return '';
  const { orientation, before, after } = i;
  const displayStyle = `display: ${
    orientation ===
    (('HORIZONTAL' as unknown) as WInFlowThemeComponentAssetSummaryView.OrientationEnum)
      ? 'inline-block'
      : 'block'
  }`;
  const result = css`
    ${displayStyle};
    :before {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      position: relative;
      ${mapPseudoElementToCSS(before)}
    }

    :after {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      ${mapPseudoElementToCSS(after)}
    }
  `;
  return result;
};

export const mapBackgroundToCSS = (b?: WThemeComponentBackgroundView): SerializedStyles | '' => {
  if (!b) return '';
  const { background_color, background_image } = b;
  const { vertical_repeat, image_url } = background_image || {};
  const fixed = false;

  const mapRepeatToCssProperty = (v: WThemeComponentBackgroundImageView.VerticalRepeatEnum) => {
    if (v === (('NO_REPEAT' as unknown) as WThemeComponentBackgroundImageView.VerticalRepeatEnum)) {
      return 'no-repeat';
    }
    return ((v as unknown) as string).toLowerCase();
  };

  return css`
    background-color: ${background_color?.value ? `#${background_color.value}` : 'transparent'};
    ${background_image ? `background-image: url(${image_url});` : ''}
    background-repeat: no-repeat ${mapRepeatToCssProperty(
      vertical_repeat || (('' as unknown) as WThemeComponentBackgroundImageView.VerticalRepeatEnum)
    )};
    background-size: 100% auto;
    background-attachment: ${fixed ? 'fixed' : 'inherit'};
  `;
};

export const mapColorViewToCSSValue = (c?: WThemeComponentColorView): string => {
  if (!c) return '';
  const { value } = c;
  return `#${value};`;
};

export const mapFontColorToBgOverlayWithOpacity = (color: string): string => {
  const { r, g, b } = hexToRgb(color) || {};
  const isDark = isDarkColor(color);
  return `rgba(${r}, ${g}, ${b}, ${isDark ? '.08' : '.16'})`;
};
